import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { Layout, Scope } from '../../components/viewToggleMetrics';
import type { CityFile } from '../../shared/cityFile';
import type { Opportunity, WallClock } from '../../shared/opportunity';
import type { SwitchKey, TimeSlot, Weekday } from '../../shared/taxonomy';
import { resolveOrigin, type AddressOrigin, type OriginKind } from '../travel/home';
import { useTravelTimes, type TravelData } from '../travel/useTravelTimes';
import type { LatLng, TravelMode } from '../travel/walkshed';
import {
  EMPTY_FILTERS,
  applyFilters,
  collapseSeries,
  defaultWindow,
  facetCounts,
  findLoosenSuggestion,
  relax,
  type Card,
  type FacetCounts,
  type FilterResult,
  type FilterState,
  type ListContext,
  type LoosenSuggestion,
} from './filters';
import { fromSearchParams, sharedFromSearch, toSearch } from './urlState';

/**
 * Explore's filter state and everything worked out from it, in one hook so
 * `App.tsx` only wires it in. Split out of App by Session 0 so the sessions
 * working on Explore (2, 5) change this file rather than App's.
 */

interface Inputs {
  file: CityFile | null;
  all: Opportunity[];
  hiddenIds: ReadonlySet<string>;
  rsvpIds: ReadonlySet<string>;
  editedById: ReadonlyMap<string, Opportunity>;
  /** `cg.home.<city>`'s point. */
  home: LatLng | null;
  /** The device's position, once it has been asked for and has arrived. */
  current: LatLng | null;
  /** A place searched in the map's popover, measured from for this session only. */
  address?: AddressOrigin | null;
  /** The mode stored with the home: a link that names none uses it. */
  homeMode?: TravelMode;
  now: WallClock;
  /** Saved runs the same filters over Going and Maybe only (contract §10). */
  scope: Scope;
  /** Written to the address beside the filters, as `?view=`. */
  layout: Layout;
}

/** What one scope shows under the current filters. */
export interface ScopeResult {
  result: FilterResult;
  /** One per series, lead first. */
  cards: Card[];
  /** "Drop Outdoor to see 41 events." Only while the list is empty. */
  suggestion: LoosenSuggestion | null;
}

export interface Explore {
  /** The filters as the current scope runs them: `savedOnly` is the scope. */
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  ctx: ListContext;
  result: FilterResult;
  /** One per series, lead first. */
  cards: Card[];
  counts: FacetCounts;
  /** What distance is measured from, now: Current or the address when chosen and known, else home, else nothing. */
  origin: { point: LatLng; kind: OriginKind } | null;
  /** TravelTime's minutes (already in `ctx`) and the isochrone the map draws (contract §17). */
  travel: TravelData;
  /** "Drop Outdoor to see 41 events." Only while the list is empty. */
  suggestion: LoosenSuggestion | null;
  setQuery: (query: string) => void;
  toggleWeekday: (key: Weekday) => void;
  toggleSlot: (slot: TimeSlot) => void;
  toggleRole: (key: string) => void;
  toggleTopic: (key: string) => void;
  toggleSwitch: (key: SwitchKey) => void;
  setWithin: (minutes: number | null) => void;
  setMode: (mode: TravelMode) => void;
  setOrigin: (origin: FilterState['origin']) => void;
  resetFacet: (facet: 'weekdays' | 'slots' | 'roles' | 'topics' | 'switches') => void;
  resetAll: () => void;
  loosen: (s: LoosenSuggestion) => void;
  /** Both scopes, so a phone's page swipe can draw the one it is moving to. */
  byScope: Record<Scope, ScopeResult>;
}

function toggled<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/** The paths the filters belong to: both scopes, and Explore with a callout open. */
const onExplorePath = (pathname: string) =>
  pathname === '/explore' || pathname === '/saved' || pathname.startsWith('/e/');

export function useExplore({ file, all, hiddenIds, rsvpIds, editedById, home, current, address = null, homeMode, now, scope, layout }: Inputs): Explore {
  const location = useLocation();
  const navigate = useNavigate();
  // The query string on arrival is the filters (contract §4); from then on the filters write it.
  const [base, setFilters] = useState<FilterState>(() =>
    onExplorePath(location.pathname)
      ? fromSearchParams(new URLSearchParams(location.search), homeMode)
      : { ...EMPTY_FILTERS, mode: homeMode ?? EMPTY_FILTERS.mode },
  );

  // Every change is written back with `replace`, so the address can be shared
  // or reloaded without each click becoming a history entry.
  // A shared calendar's `ids=` stays in the address while it is open (Session 8).
  const shared = location.pathname === '/saved' ? sharedFromSearch(location.search) : null;
  const search = toSearch(base, layout, shared);
  useEffect(() => {
    if (onExplorePath(location.pathname) && location.search !== search) {
      navigate({ pathname: location.pathname, search }, { replace: true, state: location.state });
    }
  }, [search, location.pathname, location.search, location.state, navigate]);

  const origin = useMemo(() => resolveOrigin(base.origin, home, current, address), [base.origin, home, current, address]);

  // Real minutes and the isochrone, for whatever the origin and mode are now.
  const travel = useTravelTimes({
    origin: origin?.point ?? null,
    mode: base.mode,
    withinMinutes: base.withinMinutes,
    city: file,
    events: all,
  });

  const ctx = useMemo<ListContext>(
    () => ({
      hiddenIds,
      rsvpIds,
      editedById,
      origin: origin?.point ?? null,
      city: file,
      travel: travel.times,
      now,
      window: defaultWindow(now),
    }),
    [hiddenIds, rsvpIds, editedById, origin, file, travel.times, now],
  );

  const scoped = useMemo(
    () => ({
      explore: base.savedOnly ? { ...base, savedOnly: false } : base,
      saved: { ...base, savedOnly: true },
    }),
    [base],
  );
  const filters = scoped[scope];

  const exploreResult = useMemo(() => applyFilters(all, scoped.explore, ctx), [all, scoped.explore, ctx]);
  const savedResult = useMemo(() => applyFilters(all, scoped.saved, ctx), [all, scoped.saved, ctx]);
  const exploreCards = useMemo(() => collapseSeries(exploreResult.items), [exploreResult.items]);
  const savedCards = useMemo(() => collapseSeries(savedResult.items), [savedResult.items]);
  // The panel counts what the scope on screen runs over.
  const counts = useMemo(() => facetCounts(all, filters, ctx), [all, filters, ctx]);

  const labels = useMemo(() => {
    const role = new Map(file?.taxonomy.roles.map((r) => [r.key, r.label]));
    const topic = new Map(file?.taxonomy.topics.map((t) => [t.key, t.label]));
    return { roleLabel: (k: string) => role.get(k) ?? k, topicLabel: (k: string) => topic.get(k) ?? k };
  }, [file]);
  // Nothing in range is empty even with a "Distance unknown" group left: the suggestion is the way out.
  const exploreEmpty = exploreCards.length === 0;
  const savedEmpty = savedCards.length === 0;
  const exploreSuggestion = useMemo(
    () => (exploreEmpty ? findLoosenSuggestion(all, scoped.explore, ctx, labels) : null),
    [exploreEmpty, all, scoped.explore, ctx, labels],
  );
  const savedSuggestion = useMemo(
    () => (savedEmpty ? findLoosenSuggestion(all, scoped.saved, ctx, labels) : null),
    [savedEmpty, all, scoped.saved, ctx, labels],
  );
  const byScope = useMemo<Record<Scope, ScopeResult>>(
    () => ({
      explore: { result: exploreResult, cards: exploreCards, suggestion: exploreSuggestion },
      saved: { result: savedResult, cards: savedCards, suggestion: savedSuggestion },
    }),
    [exploreResult, exploreCards, exploreSuggestion, savedResult, savedCards, savedSuggestion],
  );
  const { result, cards, suggestion } = byScope[scope];

  const setQuery = useCallback((query: string) => setFilters((f) => ({ ...f, query })), []);
  const toggleWeekday = useCallback((key: Weekday) => setFilters((f) => ({ ...f, weekdays: toggled(f.weekdays, key) })), []);
  const toggleSlot = useCallback((slot: TimeSlot) => setFilters((f) => ({ ...f, slots: toggled(f.slots, slot) })), []);
  const toggleRole = useCallback((key: string) => setFilters((f) => ({ ...f, roles: toggled(f.roles, key) })), []);
  const toggleTopic = useCallback((key: string) => setFilters((f) => ({ ...f, topics: toggled(f.topics, key) })), []);
  const toggleSwitch = useCallback(
    (key: SwitchKey) =>
      setFilters((f) => {
        const switches = toggled(f.switches, key);
        // In-person and Virtual are opposites: choosing one clears the other, as MCW did.
        if (key === 'inPerson' && switches.has('inPerson')) switches.delete('virtual');
        if (key === 'virtual' && switches.has('virtual')) switches.delete('inPerson');
        if (key === 'indoor' && switches.has('indoor')) switches.delete('outdoor');
        if (key === 'outdoor' && switches.has('outdoor')) switches.delete('indoor');
        return { ...f, switches };
      }),
    [],
  );
  const setWithin = useCallback((withinMinutes: number | null) => setFilters((f) => ({ ...f, withinMinutes })), []);
  const setMode = useCallback((mode: TravelMode) => setFilters((f) => ({ ...f, mode })), []);
  const setOrigin = useCallback((o: FilterState['origin']) => setFilters((f) => ({ ...f, origin: o })), []);
  const resetFacet = useCallback(
    (facet: 'weekdays' | 'slots' | 'roles' | 'topics' | 'switches') =>
      setFilters((f) => ({ ...f, [facet]: new Set() }) as FilterState),
    [],
  );
  // Reset clears the distance limit and the origin, and keeps the mode: it belongs to the home.
  const resetAll = useCallback(() => setFilters((f) => ({ ...EMPTY_FILTERS, mode: f.mode })), []);
  const loosen = useCallback((s: LoosenSuggestion) => setFilters((f) => relax(f, s.facet, s.value)), []);

  return {
    filters,
    setFilters,
    ctx,
    result,
    cards,
    counts,
    origin,
    travel,
    suggestion,
    setQuery,
    toggleWeekday,
    toggleSlot,
    toggleRole,
    toggleTopic,
    toggleSwitch,
    setWithin,
    setMode,
    setOrigin,
    resetFacet,
    resetAll,
    loosen,
    byScope,
  };
}
